/**
 * Schema.org Article JSON-LD — for blog/article detail pages. Three
 * subtypes via {@code articleType}: {@code 'BlogPosting'} (default),
 * {@code 'Article'} (generic), {@code 'NewsArticle'} (Google News
 * eligibility on date-sensitive content).
 */
import { JsonLdScript } from './_shared';
import { buildPersonSchema, type PersonSchemaInput } from './PersonSchemaJsonLd';

export type ArticleType = 'Article' | 'BlogPosting' | 'NewsArticle';

export interface ArticleSchemaJsonLdProps {
  articleType?: ArticleType;
  /** Required. Keep under 110 characters — Google truncates beyond. */
  headline: string;
  description?: string;
  image?: string;
  /** ISO 8601 publish timestamp. */
  datePublished?: string;
  /** ISO 8601 modification timestamp. Defaults to {@code datePublished}. */
  dateModified?: string;
  /** Required for Google's Article rich result. Use {@code { name: 'Editorial team' }}
   *  or the tenant name when no individual author is known. */
  author?: PersonSchemaInput;
  url: string;
  /** Active locale (ISO 639-1: "en", "tr", …). */
  inLanguage?: string;
  /** Tag list — emitted as the {@code keywords} field for AI search. */
  keywords?: string[];
  wordCount?: number;
  publisherName: string;
  publisherLogoUrl?: string;
  id?: string;
}

export function ArticleSchemaJsonLd({ id, ...input }: ArticleSchemaJsonLdProps) {
  const schema = buildArticleSchema(input);
  return <JsonLdScript schema={schema} id={id ?? 'schema-article'} />;
}

export function buildArticleSchema(input: Omit<ArticleSchemaJsonLdProps, 'id'>) {
  return {
    '@context': 'https://schema.org',
    '@type': input.articleType ?? 'BlogPosting',
    headline: input.headline,
    description: input.description,
    image: input.image,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: input.author ? buildPersonSchema(input.author) : undefined,
    url: input.url,
    mainEntityOfPage: input.url,
    inLanguage: input.inLanguage,
    keywords: input.keywords && input.keywords.length > 0 ? input.keywords.join(', ') : undefined,
    wordCount: input.wordCount,
    publisher: {
      '@type': 'Organization',
      name: input.publisherName,
      logo: input.publisherLogoUrl
        ? {
            '@type': 'ImageObject',
            url: input.publisherLogoUrl,
          }
        : undefined,
    },
  };
}
